import { Document } from '~publish/legacy/composer/composer/entities/Document'
import type { DocumentTitle } from '~publish/legacy/composer/composer/entities/Document'
import type {
  Gif,
  Image,
  Link,
  Video,
} from '~publish/legacy/composer/composer/entities/factories'
import type { OmniAsset } from '../postSlice/types'

/**
 * Converts a legacy Flux draft's separate media fields into the unified
 * OmniAsset array.
 *
 * Unlike the per-channel save path (mapMediaAssets in save/mapAssets.ts), which
 * collapses to a single media type by precedence, the channel-less
 * DraftContent.assets is a heterogeneous array. This mapper is therefore
 * additive: every populated media type is included, in the order images →
 * videos → document → gif. Mixed media (e.g. an image and a PDF together) is
 * the capability the channel-less GraphQL draft unlocks over legacy.
 *
 * Takes `videos: Video[]` (every attached video, in order) rather than a
 * draft/post's `video` field — that field holds only the first video, kept on
 * `Draft`/`Post` for legacy single-media call sites. Consuming it here would
 * silently drop every video after the first from a mixed-media post.
 *
 * An enabled link attachment stays exclusive, matching the composer UI where a
 * link card replaces media. A draft can hold a scraped link preview without it
 * being the enabled attachment, so the link only takes over when
 * `hasLinkAttachment` says it is. Only a full Document (has url/id) is included
 * — a DocumentTitle (upload in progress) is dropped because it has no
 * addressable url.
 */
export function draftMediaToAssets(
  source: {
    link: Link | null
    images: Image[]
    videos: Video[]
    gif: Gif | null
    document?: Document | DocumentTitle | null
  },
  { hasLinkAttachment }: { hasLinkAttachment: boolean },
): OmniAsset[] {
  if (hasLinkAttachment && source.link !== null) {
    return [{ kind: 'link', link: source.link }]
  }

  const assets: OmniAsset[] = source.images.map((image) => ({
    kind: 'image',
    image,
  }))

  for (const video of source.videos) {
    assets.push({ kind: 'video', video })
  }

  const doc = source.document ?? null
  if (Document.isDocument(doc)) {
    assets.push({ kind: 'document', document: doc })
  }

  if (source.gif !== null) {
    assets.push({ kind: 'gif', gif: source.gif })
  }

  return assets
}
